import { Ecommerce } from './events/ecommerce/types';
import { GAEventName } from './ga-event-name.enum';
import { KLAVIYO_CTA_TEXT } from './constants';
import { DataLayerEvent } from './types';

interface ItemProps {
  id: string;
  item_name: string;
  title?: string;
  category: string;
  quantity: number;
  price: number;
}

export const ALLOWED_BLOTOUT_EVENTS: ReadonlySet<GAEventName> = new Set([
  GAEventName.ADD_SHIPPING_INFO,
  GAEventName.ADD_PAYMENT_INFO,
  GAEventName.ADD_TO_CART,
  GAEventName.PURCHASE,
  GAEventName.HISTORY_CHANGE,
  GAEventName.NEWSLETTER_SIGNUP_SUCCESS,
  GAEventName.BUTTON_CLICK,
  GAEventName.LOGIN,
  GAEventName.SIGN_UP,
  GAEventName.VIEW_CONTENT
]);

export const logBlotoutEvent = (event: DataLayerEvent) => {
  if (process.env.NODE_ENV === 'development') return;

  const eventName = event.event || event.event_name;
  const eventEcommerce = event.ecommerce as Ecommerce | undefined;
  if (!eventName || typeof window === 'undefined' || !window.edgetag) return;

  const { edgetag } = window;

  const items = eventEcommerce?.items;
  if (ALLOWED_BLOTOUT_EVENTS.has(eventName as GAEventName)) {
    if (eventName === GAEventName.HISTORY_CHANGE) {
      edgetag('tag', 'PageView');
    }

    if (eventName === GAEventName.BUTTON_CLICK && event.click_text === KLAVIYO_CTA_TEXT) {
      const emailInput = document.querySelector(
        'form.klaviyo-form input[type="email"][name="email"]'
      ) as HTMLInputElement;

      if (emailInput) {
        const emailValue = emailInput.value;
        edgetag('tag', 'Lead', {
          category: 'Klaviyo',
          name: 'Signup'
        });
        if (emailValue) {
          edgetag('user', 'email', emailValue as string);
        }
      }
    }

    if (eventName === GAEventName.VIEW_CONTENT) {
      const contents = (event.contents as any[])?.map((item: ItemProps) => ({
        id: String(item.id),
        title: item.title || item.item_name,
        category: item.category,
        quantity: item.quantity,
        item_price: item.price
      }));
      const totalPrice = contents.reduce((acc, item) => acc + item.item_price, 0);
      edgetag('tag', 'ViewContent', {
        currency: event?.currency,
        value: event?.value || totalPrice,
        contents
      });
    }

    if (eventName === GAEventName.ADD_TO_CART) {
      edgetag('tag', 'AddToCart', {
        currency: eventEcommerce?.currency,
        value: eventEcommerce?.value ? parseFloat(eventEcommerce?.value) : 0.0,
        contents:
          items &&
          items.map((item) => ({
            id: String(item.item_id),
            quantity: item.quantity,
            item_price: item.price ? parseFloat(item.price) : 0.0
          }))
      });
    }

    if (eventName === GAEventName.PURCHASE) {
      if ((event as any).checkoutData?.customer_data) {
        // eslint-disable-next-line @typescript-eslint/naming-convention
        const { email, first_name, last_name, phone } = (event as any).checkoutData.customer_data;
        edgetag('data', {
          email,
          firstName: first_name,
          lastName: last_name,
          phone
        });
      }
      edgetag('tag', 'Purchase', {
        currency: eventEcommerce?.currency,
        value: eventEcommerce?.value,
        orderId: eventEcommerce?.transaction_id,
        contents:
          items &&
          items.map((item) => ({
            id: item.item_id,
            quantity: item.quantity,
            price: item.price
          }))
      });
    }

    if (eventName === GAEventName.ADD_SHIPPING_INFO) {
      if (event?.email) {
        edgetag('user', 'email', event.email as string);
      }
      edgetag('tag', 'AddShippingInfo', {
        currency: eventEcommerce?.currency,
        value: eventEcommerce?.value,
        contents:
          items &&
          items.map((item) => ({
            id: item.item_id,
            quantity: item.quantity,
            price: item.price
          }))
      });
    }

    if (eventName === GAEventName.ADD_PAYMENT_INFO) {
      edgetag('tag', 'AddPaymentInfo', {
        currency: eventEcommerce?.currency,
        value: eventEcommerce?.value,
        contents:
          items &&
          items.map((item) => ({
            id: item.item_id,
            quantity: item.quantity,
            price: item.price
          }))
      });
    }

    if (eventName === GAEventName.NEWSLETTER_SIGNUP_SUCCESS) {
      edgetag('tag', 'Lead', {
        category: 'Newsletter',
        name: 'Signup',
        source: event.location || 'unknown'
      });
      if (event.email) {
        edgetag('user', 'email', event.email as string);
      }
    }

    if (eventName === GAEventName.LOGIN) {
      if (event.email) {
        edgetag('user', 'email', event.email as string);
      }
    }

    if (eventName === GAEventName.SIGN_UP) {
      if (event.email) {
        edgetag('data', {
          email: event.email as string,
          firstName: event.first_name as string,
          lastName: event.last_name as string
        });
      }
    }
  }
};
